import React from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { 
  TextField, 
  Select, 
  FormControl, 
  InputLabel, 
  OutlinedInput, 
  MenuItem, 
  Box, 
  IconButton,
  FormHelperText
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";

const EmailForm = ({ templateOptions, receipentOptions, initialValues, onFormChange }) => {
  // Validation schema
  const validationSchema = Yup.object({
    recipientName: Yup.array()
      .min(1, "At least one recipient is required")
      .required("Recipients are required"),
    templateName: Yup.string().required("Template is required"),
  });

  // Initialize Formik
  const formik = useFormik({
    initialValues: initialValues || {
      recipientName: [],
      templateName: "",
    },
    validationSchema,
    // We're not handling submission in this component, that's in the parent
    onSubmit: () => {},
    validateOnChange: true,
    validateOnBlur: true
  });

  // Update parent component when form values change
  React.useEffect(() => {
    if (onFormChange) {
      onFormChange(formik.values);
    }
  }, [formik.values, onFormChange]);

  return (
    <div style={{ marginTop: "6px" }}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "center", justifyContent: "space-around" }}>
        <FormControl 
          fullWidth 
          variant="outlined" 
          error={formik.touched.recipientName && Boolean(formik.errors.recipientName)}
        >
          <InputLabel id="recipient-label">Recipient</InputLabel>
          <Select
            labelId="recipient-label"
            id="recipientName"
            name="recipientName"
            multiple
            value={formik.values.recipientName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            input={
              <OutlinedInput 
                label="Recipient" 
                endAdornment={
                  formik.values.recipientName.length > 0 && (
                    <IconButton
                      onClick={() => formik.setFieldValue("recipientName", [])}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  )
                }
              />
            }
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 250,
                },
              },
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
            }}
            sx={{
              width: "300px",
              height: "48px",
              "& .MuiSelect-select": {
                padding: "6px 14px",
              },
            }}
          >
            {receipentOptions.map((name) => (
              <MenuItem key={name.arcimedes_row_id} value={name.arcimedes_row_id}>
                {`${name.first_name} ${name.last_name}`}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.recipientName && formik.errors.recipientName && (
            <FormHelperText error>{formik.errors.recipientName}</FormHelperText>
          )}
        </FormControl>

        <FormControl 
          fullWidth 
          variant="outlined"
          error={formik.touched.templateName && Boolean(formik.errors.templateName)}
        >
          <InputLabel id="template-label">Template</InputLabel>
          <Select
            labelId="template-label"
            id="templateName"
            name="templateName"
            value={formik.values.templateName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            input={
              <OutlinedInput 
                label="Template"
                endAdornment={
                  formik.values.templateName.length > 0 && (
                    <IconButton
                      onClick={() => formik.setFieldValue("templateName", "")}
                      edge="end"
                      size="small"
                    >
                      <ClearIcon />
                    </IconButton>
                  )
                }
              />
            }
            MenuProps={{
              PaperProps: {
                sx: {
                  maxHeight: 250,
                },
              },
              anchorOrigin: {
                vertical: "bottom",
                horizontal: "left",
              },
              transformOrigin: {
                vertical: "top",
                horizontal: "left",
              },
            }}
            sx={{
              width: "300px",
              height: "48px",
              "& .MuiSelect-select": {
                padding: "6px 14px",
              },
            }}
          >
            {templateOptions.map((option, index) => (
              <MenuItem key={index} value={option.template_name}>
                {option.template_name}
              </MenuItem>
            ))}
          </Select>
          {formik.touched.templateName && formik.errors.templateName && (
            <FormHelperText error>{formik.errors.templateName}</FormHelperText>
          )}
        </FormControl>
      </Box>
    </div>
  );
};

export default EmailForm;